import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { CASE_STUDIES } from "@/data/testimonials";
import { api, trackEvent } from "@/lib/api";
import { ArrowRight, CircleCheck, Download, Loader2 } from "lucide-react";

const LAUNCH_POST_TEXT = `We just shipped AuditVanguard AIMS — the AI-Powered Enterprise GRC & AIMS Platform.

Compliance in 2026 needs three things at once:
• Automated evidence from AWS, GitHub and Okta — not screenshots
• Real cross-mapping across SOC 2, ISO 27001, ISO 42001, PCI DSS, HIPAA and the new EU AI Act (Articles 9, 10, 13, 14, 15, 62)
• Tamper-evident audit trails your auditor can independently check

AuditVanguard AIMS brings all three into one workspace. Every auditor action, file download and PDF export is written to a SHA-256 hash-chained log and exportable as a signed file any recipient can independently verify.

Live now: https://auditvanguard.com

Built by GSGD Field Solutions LLC.

#GRC #AIGovernance #EUAIAct #SOC2 #ISO42001 #ComplianceAutomation #AuditVanguard`;

const LaunchShareBlock = () => {
    const [copied, setCopied] = useState(false);
    const shareUrl = "https://auditvanguard.com/";
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        "AuditVanguard AIMS is live — automated evidence ingestion, EU AI Act coverage, and cryptographically verifiable audit trails. "
    )}&url=${encodeURIComponent(shareUrl)}`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(LAUNCH_POST_TEXT);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // clipboard copy is best-effort
        }
    };

    return (
        <div
            className="mt-2 rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-6 space-y-4"
            data-testid="launch-share-block"
        >
            <div className="mono-label status-pos flex items-center gap-1.5">
                <CircleCheck className="h-3 w-3" strokeWidth={1.5} />
                Announcement ready
            </div>
            <div className="font-display font-semibold text-base">Share the launch</div>

            <div className="rounded-xl border border-border bg-background overflow-hidden">
                <img
                    src="/linkedin-launch.svg"
                    alt="AuditVanguard AIMS just launched"
                    className="w-full aspect-[1200/627] object-cover"
                    data-testid="launch-share-image"
                />
            </div>

            <div>
                <div className="mono-label text-muted-foreground mb-1.5">Post copy</div>
                <pre
                    className="whitespace-pre-wrap text-xs leading-relaxed rounded-xl border border-border bg-background p-3 max-h-56 overflow-y-auto font-sans"
                    data-testid="launch-post-copy"
                >{LAUNCH_POST_TEXT}</pre>
            </div>

            <div className="flex flex-wrap gap-2">
                <Button
                    type="button"
                    size="sm"
                    onClick={copy}
                    className="rounded-md font-display bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="launch-copy-post-btn"
                >
                    {copied ? (
                        <span className="inline-flex items-center gap-1.5">
                            Copied <CircleCheck className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </span>
                    ) : (
                        "Copy post text"
                    )}
                </Button>
                <Button
                    asChild
                    size="sm"
                    variant="outline"
                    className="rounded-md font-display border-border/70"
                >
                    <a
                        href={linkedInUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="launch-share-linkedin"
                    >
                        Open LinkedIn share
                    </a>
                </Button>
                <Button
                    asChild
                    size="sm"
                    variant="outline"
                    className="rounded-md font-display border-border/70"
                >
                    <a
                        href={twitterUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="launch-share-x"
                    >
                        Share on X
                    </a>
                </Button>
                <Button
                    asChild
                    size="sm"
                    variant="ghost"
                    className="rounded-md font-display"
                >
                    <a href="/linkedin-launch.svg" download data-testid="launch-download-image">
                        Download image
                    </a>
                </Button>
            </div>

            <div className="mono-label text-muted-foreground">
                Tip · pair the copy with the SVG on any post. The image is 1200 × 627, LinkedIn&apos;s
                recommended dimensions.
            </div>
        </div>
    );
};

export const CaseStudiesSection = () => {
    const [selectedCase, setSelectedCase] = useState(null);
    const [caseDetail, setCaseDetail] = useState(null);
    const [caseLoading, setCaseLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    // Lazy-load the fuller narrative from the backend when a tile is clicked.
    useEffect(() => {
        if (!selectedCase) { setCaseDetail(null); return; }
        setCaseLoading(true);
        setCaseDetail(null);
        (async () => {
            try {
                const { data } = await api.get("/case-studies");
                setCaseDetail(data.find((d) => d.slug === selectedCase) || null);
            } catch {
                setCaseDetail(null);
            } finally {
                setCaseLoading(false);
            }
        })();
    }, [selectedCase]);

    const downloadCaseStudyPdf = async (slug) => {
        setDownloading(true);
        try {
            const resp = await api.get(`/case-studies/${slug}/pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `auditvanguard-case-study-${slug}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            trackEvent("case_study_pdf_downloaded", { slug });
        } catch (err) {
            // download is best-effort; the button simply re-enables
            console.warn("Case study PDF download failed:", err);
        } finally { setDownloading(false); }
    };

    return (
        <>
            <section
                className="max-w-7xl mx-auto px-5 sm:px-10 py-16 lg:py-24 border-t border-border/60"
                data-testid="case-studies-section"
            >
                <div className="max-w-3xl mb-10">
                    <div className="mono-label mb-3 inline-flex items-center gap-2">
                        <CircleCheck className="h-3 w-3" strokeWidth={1.5} />
                        Consultants getting clients audit-ready with AuditVanguard
                    </div>
                    <h2 className="mt-4 text-muted-foreground max-w-xl text-base font-normal" data-testid="testimonials-disclosure">
                        Pilot customer results. Shared with permission.
                    </h2>
                </div>

                <button
                    type="button"
                    onClick={() => setSelectedCase("auditvanguard-aims-launch")}
                    className="w-full text-left rounded-xl border border-emerald-500/40 bg-emerald-500/5 px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-1 mb-6 hover:bg-emerald-500/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid="aims-launch-banner"
                >
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-500/10 status-live border border-emerald-500/30 whitespace-nowrap">
                        New
                    </span>
                    <span className="text-sm font-medium min-w-0">
                        AuditVanguard AIMS is live — automated evidence ingestion, EU AI Act coverage, and verifiable audit trails.
                    </span>
                    <span className="text-sm underline underline-offset-4 text-muted-foreground ml-auto whitespace-nowrap">
                        Read →
                    </span>
                </button>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {CASE_STUDIES.filter((c) => c.slug !== "auditvanguard-aims-launch").map((c) => (
                        <button
                            key={c.firm}
                            type="button"
                            onClick={() => setSelectedCase(c.slug)}
                            className={`text-left rounded-2xl border bg-card p-6 sm:p-8 flex flex-col hover:-translate-y-1 hover:border-slate-900/50 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${c.badge ? "border-emerald-500/50" : "border-border"}`}
                            data-testid={`case-study-${c.initial.toLowerCase()}`}
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-xl bg-slate-900 text-white grid place-items-center font-display font-semibold shrink-0">
                                    {c.initial}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="font-display font-semibold min-w-0 break-words">
                                        {c.firm}
                                    </div>
                                    <div className="mt-1">
                                        {c.verified && c.badge && (
                                            <span className="mono-label px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">
                                                {c.badge}
                                            </span>
                                        )}
                                        {!c.verified && (
                                            <span
                                                className="mono-label px-1.5 py-0.5 rounded-full border border-border text-muted-foreground"
                                                data-testid={`pilot-chip-${c.initial.toLowerCase()}`}
                                            >
                                                Pilot feedback
                                            </span>
                                        )}
                                    </div>
                                    <div className="mono-label text-muted-foreground mt-1">
                                        {c.specialty}
                                    </div>
                                </div>
                                <span className="mono-label text-muted-foreground hidden sm:inline-flex items-center gap-1">
                                    Read <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
                                </span>
                            </div>
                            <blockquote className="text-base leading-relaxed">
                                &ldquo;{c.quote}&rdquo;
                            </blockquote>
                            <div className="mono-label mt-4 text-muted-foreground">
                                — {c.author}
                                {c.verified && c.link && (
                                    <a
                                        href={c.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="ml-2 underline hover:text-foreground"
                                        data-testid={`case-link-${c.initial.toLowerCase()}`}
                                    >
                                        Profile ↗
                                    </a>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-6 pt-6 border-t border-border/60">
                                {c.stats.map((s) => (
                                    <div key={s.label} data-testid={`case-stat-${c.initial.toLowerCase()}-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                        <div className="font-display text-[28px] font-bold leading-tight">
                                            {s.value}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-0.5">
                                            {s.label}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </button>
                    ))}
                </div>
            </section>

            {/* Case study modal — fuller narrative + PDF download */}
            <Dialog open={!!selectedCase} onOpenChange={(o) => { if (!o) setSelectedCase(null); }}>
                <DialogContent
                    className="max-w-2xl bg-card border-border p-0 overflow-hidden"
                    data-testid="case-study-modal"
                >
                    <div className="px-6 sm:px-8 pt-6 sm:pt-8 pb-4 border-b border-border">
                        <DialogHeader>
                            {caseDetail && (
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-12 h-12 rounded-xl bg-slate-900 text-white grid place-items-center font-display font-semibold">
                                        {caseDetail.initial}
                                    </div>
                                    <div className="min-w-0">
                                        <DialogTitle className="font-display text-xl font-semibold">
                                            {caseDetail.firm} · {caseDetail.location}
                                        </DialogTitle>
                                        <DialogDescription className="mono-label text-muted-foreground text-[11px]">
                                            {caseDetail.specialty}
                                        </DialogDescription>
                                    </div>
                                </div>
                            )}
                            {!caseDetail && (
                                <DialogTitle className="font-display text-xl font-semibold">
                                    Case study
                                </DialogTitle>
                            )}
                        </DialogHeader>
                    </div>

                    <div className="px-6 sm:px-8 py-6 space-y-6 max-h-[65vh] overflow-y-auto">
                        {caseLoading && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                                Loading full story…
                            </div>
                        )}
                        {caseDetail && (
                            <>
                                <blockquote className="border-l-2 border-slate-900 pl-4 italic text-base leading-relaxed">
                                    &ldquo;{caseDetail.quote}&rdquo;
                                </blockquote>
                                <div className="mono-label text-muted-foreground">— {caseDetail.author}</div>

                                <div>
                                    <div className="font-display font-semibold text-sm mb-1.5">Challenge</div>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{caseDetail.challenge}</p>
                                </div>
                                <div>
                                    <div className="font-display font-semibold text-sm mb-1.5">Approach</div>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{caseDetail.approach}</p>
                                </div>
                                <div>
                                    <div className="font-display font-semibold text-sm mb-1.5">Results</div>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{caseDetail.results}</p>
                                </div>

                                {caseDetail.metrics?.length > 0 && (
                                    <div>
                                        <div className="font-display font-semibold text-sm mb-2">Impact</div>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                            {caseDetail.metrics.map((m) => (
                                                <div key={m.label} className="rounded-xl border border-border bg-background/40 p-3" data-testid={`case-modal-metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                                    <div className="font-display font-semibold text-lg">{m.value}</div>
                                                    <div className="mono-label text-muted-foreground text-xs mt-0.5">{m.label}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {caseDetail.timeline?.length > 0 && (
                                    <div>
                                        <div className="font-display font-semibold text-sm mb-2">Timeline</div>
                                        <ol className="space-y-1.5 text-sm">
                                            {caseDetail.timeline.map(([when, what]) => (
                                                <li key={when} className="flex gap-3">
                                                    <span className="mono-label text-muted-foreground shrink-0 w-16">{when}</span>
                                                    <span className="text-muted-foreground leading-relaxed">{what}</span>
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                )}

                                {caseDetail.slug === "auditvanguard-aims-launch" && (
                                    <LaunchShareBlock />
                                )}
                            </>
                        )}
                    </div>

                    <div className="px-6 sm:px-8 py-4 border-t border-border flex items-center justify-between flex-wrap gap-2 bg-muted/20">
                        <Link to="/register" className="mono-label hover:text-foreground transition-colors" data-testid="case-modal-cta-register">
                            Start your own compliance audit →
                        </Link>
                        <Button
                            onClick={() => caseDetail && downloadCaseStudyPdf(caseDetail.slug)}
                            disabled={!caseDetail || downloading}
                            className="rounded-md font-display bg-slate-900 hover:bg-slate-800 text-white"
                            data-testid="case-modal-download-pdf"
                        >
                            {downloading ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" strokeWidth={1.5} />
                            ) : (
                                <Download className="h-4 w-4 mr-2" strokeWidth={1.5} />
                            )}
                            Download PDF
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
